// UnlockTrialCapModal.hook.ts owns the state machine for the Lead Agent
// trial-cap UNLOCK modal (RET-1633 / U10). Adapted from UpgradeTierModal.hook.ts:
// same confirm → processing → success | error | blocked shape and the same
// client-side-only 3DS step, but it speaks the U8 `POST .../unlock` endpoint and
// has NO preview fetch — the offer already carries the exact per-tier amounts, so
// the charge sends only `{ tier }` and the backend mints a fresh idempotency key
// per user-initiated retry (a retry re-POSTs the SAME body verbatim).
//
// Terminal branches:
//   - 200 ok               → success; `pending` from the payload's
//                            `provisionPending` (copy says "procesando", never
//                            claims the cap is live yet).
//   - 200 requires_action  → 3DS via `stripe.confirmCardPayment(clientSecret)`,
//                            CLIENT-SIDE ONLY (NEVER a backend confirm — it
//                            cancels the sub on a declined 3DS, verified in A11).
//                            3DS success → success with `pending: true` (the
//                            webhook completes provisioning); 3DS decline/abandon
//                            → retryable error, NO success claim.
//   - 200 failed           → retryable inline error with the decline `message`
//                            (+ the update-card link, in the component).
//   - thrown 409 in_progress    → retryable error ("procesando otro intento").
//   - thrown 409 already_extended → blocked (the one extension is spent).
//   - thrown other 4xx     → blocked (readBffError).
//   - thrown 424 / 5xx     → retryable error.
//
// Two race windows cross an await (POST, confirmCardPayment); after each, bail if
// the active workspace changed (workspaceId is a prop, captured into a ref kept
// fresh each render).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { useStripe } from '@stripe/react-stripe-js';
import { LeadAgentUnlockOfferService } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentUnlockOffer.service';
import type {
  tLeadAgentSelectableTier,
  tLeadAgentUnlockOffer,
  tLeadAgentUnlockTierOffer,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { extractPaymentIntentData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import type { tRenewalProjectionWireData } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';

/**
 * Why a retryable error was raised — drives which copy the component shows:
 * - decline:     a payment decline (200 failed) or a declined/abandoned 3DS →
 *                surface the decline message + the "update card" deep link.
 * - in_progress: a concurrent unlock attempt is already processing (409) → tell
 *                the Owner to wait and retry; no update-card link.
 * - generic:     transient comms (424), 5xx, or a local/unknown fault.
 */
export type tUnlockErrorReason = 'decline' | 'in_progress' | 'generic';

/**
 * View machine. `confirm` is the tier-selection + charge step; `processing` is
 * the in-flight POST / 3DS; `blocked` is a non-retryable business rule (4xx);
 * `error` is retryable (decline, in-progress, transient 424, local/unknown).
 */
export type tUnlockView =
  | { status: 'confirm' }
  | { status: 'processing' }
  | { status: 'success'; tier: tLeadAgentSelectableTier; pending: boolean }
  | {
      status: 'error';
      reason: tUnlockErrorReason;
      /** Inline decline reason from Stripe, when present; else null. */
      message: string | null;
    }
  | {
      status: 'blocked';
      /** Backend `{code, message}` for the business-rule block. */
      code: string | null;
      message: string | null;
    };

export type tUseUnlockTrialCapModalArgs = {
  workspaceId: string;
  offer: tLeadAgentUnlockOffer;
};

/**
 * The next-renewal projection, fetched per selected tier. Same endpoint and
 * same states the acceptance/reactivation/upgrade modals use — leadbilling
 * computes the deferred Lead Agent charge that Stripe cannot preview.
 */
export type tUnlockProjection =
  | { status: 'idle' | 'loading' | 'error'; data: null }
  | { status: 'ready'; data: tRenewalProjectionWireData };

export type tUseUnlockTrialCapModal = {
  view: tUnlockView;
  /** The next-renewal breakdown for the selected tier. */
  projection: tUnlockProjection;
  /** The priced tiers on offer (Growth/Scale — never Starter, server-enforced). */
  tiers: tLeadAgentUnlockTierOffer[];
  selectedTier: tLeadAgentSelectableTier;
  /** The priced entry for the selected tier, or null when the offer carries none. */
  selectedTierOffer: tLeadAgentUnlockTierOffer | null;
  /** Confirm is enabled only when a priced tier exists and we are not in flight. */
  confirmDisabled: boolean;
  selectTier: (tier: tLeadAgentSelectableTier) => void;
  /**
   * Performs the unlock POST (+ 3DS when required). Reused as the retry action.
   * Returns whether THIS call actually started a charge attempt — false when the
   * synchronous in-flight latch swallowed it (a same-tick double click) or no
   * priced tier is selected — so the caller can gate a once-per-attempt side
   * effect (analytics) on a real attempt.
   */
  confirm: () => boolean;
  /** Re-fetches the projection after a projection error. */
  retryProjection: () => void;
};

// 3DS watchdog (REL-1): `stripe.confirmCardPayment` can hang indefinitely if the
// bank's challenge iframe never resolves (tab backgrounded, network stall). The
// modal blocks close + disables cancel while processing, so without a ceiling the
// Owner is stranded on the spinner forever. Cap the wait and fall back to a
// retryable generic error.
const CONFIRM_CARD_PAYMENT_TIMEOUT_MS = 90_000;

/** Sentinel resolved by the watchdog when the raced promise outlived the cap. */
type tConfirmTimeout = { timedOut: true };

/**
 * Races a promise against a timeout, clearing the timer either way so a resolved
 * promise never leaves a dangling handle (matters under fake timers in tests).
 */
function raceWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
): Promise<T | tConfirmTimeout> {
  let timer: ReturnType<typeof setTimeout>;
  const watchdog = new Promise<tConfirmTimeout>((resolve) => {
    timer = setTimeout(() => resolve({ timedOut: true }), ms);
  });
  return Promise.race([promise, watchdog]).finally(() => clearTimeout(timer));
}

type tBffErrorBody = { code?: unknown; message?: unknown };

/** Reads the bff `{code, message}` body off an Axios 4xx, both optional. */
function readBffError(error: unknown): {
  code: string | null;
  message: string | null;
} {
  const data = (error as AxiosError<tBffErrorBody> | undefined)?.response?.data;
  const code = typeof data?.code === 'string' ? data.code : null;
  const message = typeof data?.message === 'string' ? data.message : null;
  return { code, message };
}

function statusOf(error: unknown): number | undefined {
  return (error as AxiosError | undefined)?.response?.status;
}

/**
 * Maps a thrown unlock error to a terminal view. 409 forks on the code:
 * only the explicit `in_progress` code is retryable ("another attempt is
 * processing"); every other/unknown 409 (`already_extended` and anything the
 * backend adds later) is a business BLOCK — fail closed, consistent with the
 * rest of this module's parse rather than assuming an unknown code is safe to
 * retry. Other 4xx are business BLOCKS; 424/5xx/local are retryable.
 */
function viewForThrownError(error: unknown): tUnlockView {
  const status = statusOf(error);
  const { code, message } = readBffError(error);
  if (status === 409) {
    if (code === 'in_progress') {
      return { status: 'error', reason: 'in_progress', message: null };
    }
    return { status: 'blocked', code, message };
  }
  if (status === 424) {
    return { status: 'error', reason: 'generic', message: null };
  }
  if (status !== undefined && status >= 400 && status < 500) {
    return { status: 'blocked', code, message };
  }
  return { status: 'error', reason: 'generic', message: null };
}

export function useUnlockTrialCapModal(
  args: tUseUnlockTrialCapModalArgs,
): tUseUnlockTrialCapModal {
  const { workspaceId, offer } = args;
  const stripe = useStripe();

  const tiers = useMemo(() => offer.tiers ?? [], [offer]);
  // Recommended tier leads (R4): default to the server's suggestion when it is
  // one of the priced tiers, else the first priced tier (Starter is never here).
  const defaultTier = useMemo<tLeadAgentSelectableTier>(() => {
    const suggested = offer.suggestedTier;
    if (suggested && tiers.some((entry) => entry.tier === suggested)) {
      return suggested;
    }
    return tiers[0]?.tier ?? 'GROWTH';
  }, [offer.suggestedTier, tiers]);

  const [selectedTier, setSelectedTier] =
    useState<tLeadAgentSelectableTier>(defaultTier);
  const [view, setView] = useState<tUnlockView>({ status: 'confirm' });

  /*
   * The next-renewal projection, per selected tier.
   *
   * The unlock modal shipped without one: it charged today and said nothing
   * about what the subscription becomes at renewal. Its three sibling
   * checkouts (acceptance, reactivation, upgrade) all render this breakdown;
   * this was the only one that did not.
   *
   * Each fetch aborts the previous one, so a fast tier switch cannot apply a
   * stale breakdown (the ConfirmTierModal rule). `trialEnd` is this flow's
   * equivalent of the acceptance row's `nextPaymentAt`: the date the bridge
   * charge lands (MLA-77).
   */
  const [projection, setProjection] = useState<tUnlockProjection>({
    status: 'idle',
    data: null,
  });
  const [projectionAttempt, setProjectionAttempt] = useState(0);
  const retryProjection = useCallback(() => {
    setProjectionAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (!workspaceId || !selectedTier) return undefined;
    const controller = new AbortController();
    setProjection({ status: 'loading', data: null });
    SubscriptionManagerService.getRenewalProjection(
      workspaceId,
      // `origin` is the acceptance cohort, which this flow does not hold — the
      // unlock offer has no acceptance row. Omitted, as the service allows.
      { tier: selectedTier, nextPaymentAt: offer.trialEnd ?? null },
      controller.signal,
    )
      .then((data) => setProjection({ status: 'ready', data }))
      .catch((error) => {
        // An aborted fetch is a superseded one, never a failure to report.
        if ((error as AxiosError | undefined)?.code === 'ERR_CANCELED') return;
        setProjection({ status: 'error', data: null });
      });
    return () => controller.abort();
  }, [workspaceId, selectedTier, offer.trialEnd, projectionAttempt]);

  const selectedTierOffer = useMemo(
    () => tiers.find((entry) => entry.tier === selectedTier) ?? null,
    [tiers, selectedTier],
  );

  // Unmount guard: the confirm flow crosses up to two awaits (POST + 3DS), each
  // followed by a setView. If the modal unmounts mid-flight, bail instead of a
  // setState on an unmounted component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Synchronous in-flight latch — `view==='processing'` is async React state, so
  // two same-tick clicks would both read the pre-update value and fire two POSTs.
  const inFlightRef = useRef(false);
  // Active workspace captured fresh each render — read after every await to bail
  // if the user switched workspaces mid-flight.
  const activeWorkspaceIdRef = useRef(workspaceId);
  activeWorkspaceIdRef.current = workspaceId;

  // Keep the selected tier valid if the offer's tier set changes under us (e.g. a
  // background offer refetch). Never fires mid-charge or after a terminal success.
  useEffect(() => {
    if (view.status === 'processing' || view.status === 'success') return;
    if (tiers.length > 0 && !tiers.some((entry) => entry.tier === selectedTier)) {
      setSelectedTier(defaultTier);
      if (view.status !== 'confirm') setView({ status: 'confirm' });
    }
  }, [tiers, selectedTier, defaultTier, view.status]);

  const runConfirm = useCallback(async () => {
    // Synchronous double-submit guard.
    if (inFlightRef.current) return;
    if (!selectedTierOffer) return;

    const requestWorkspaceId = activeWorkspaceIdRef.current;
    const tierToConfirm = selectedTier;

    // Post-await guard shared by both race windows (POST + 3DS): bail if the
    // modal unmounted or the active workspace changed mid-flight. Returns true
    // when the caller must stop (and has already released the latch / reset the
    // view so the modal never strands on the spinner). The charge stays
    // attributed to `requestWorkspaceId` regardless.
    const bailedOnStaleContext = (): boolean => {
      if (!isMountedRef.current) {
        inFlightRef.current = false;
        return true;
      }
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) {
        inFlightRef.current = false;
        setView({ status: 'confirm' });
        return true;
      }
      return false;
    };

    inFlightRef.current = true;
    setView({ status: 'processing' });

    try {
      // Retry re-POSTs the SAME `{ tier }` — the backend mints the fresh key.
      const result = await LeadAgentUnlockOfferService.performUnlock(
        requestWorkspaceId,
        { tier: tierToConfirm },
      );
      // Race window 1 (post-POST).
      if (bailedOnStaleContext()) return;

      if (result.status === 'ok') {
        setView({
          status: 'success',
          // Honor the backend's authoritative tier echo; fall back to the tier
          // we charged only when the payload omits it (older bff deploys).
          tier: result.tier ?? tierToConfirm,
          pending: result.provisionPending,
        });
        return;
      }

      if (result.status === 'failed') {
        // 200 + failed = payment decline (retryable). Surface the message.
        setView({ status: 'error', reason: 'decline', message: result.message });
        return;
      }

      // requires_action: confirm the 3DS challenge CLIENT-SIDE. NEVER call a
      // backend confirm endpoint here (A11: it cancels the subscription on a
      // declined 3DS).
      const clientSecret = result.clientSecret;
      if (!stripe || !clientSecret) {
        setView({ status: 'error', reason: 'generic', message: null });
        return;
      }

      const confirmation = await raceWithTimeout(
        stripe.confirmCardPayment(clientSecret),
        CONFIRM_CARD_PAYMENT_TIMEOUT_MS,
      );
      // Race window 2 (post-3DS).
      if (bailedOnStaleContext()) return;

      if ('timedOut' in confirmation) {
        // The 3DS challenge never resolved within the cap — retryable. We make
        // NO success claim; a genuinely-succeeded PI is finalized by the webhook.
        setView({ status: 'error', reason: 'generic', message: null });
        return;
      }

      const { status } = extractPaymentIntentData(confirmation);
      if (confirmation.error || status !== 'succeeded') {
        // Declined / abandoned 3DS — retryable. NO success claim; the webhook
        // handles a genuinely-succeeded PI, we never confirm against the backend.
        setView({
          status: 'error',
          reason: 'decline',
          message: confirmation.error?.message ?? null,
        });
        return;
      }

      // 3DS succeeded: the charge is processing and the webhook completes
      // provisioning (accept + cap raise). Always the pending copy here.
      setView({
        status: 'success',
        tier: result.tier ?? tierToConfirm,
        pending: true,
      });
    } catch (error) {
      if (!isMountedRef.current) return;
      if (activeWorkspaceIdRef.current !== requestWorkspaceId) return;
      setView(viewForThrownError(error));
    } finally {
      inFlightRef.current = false;
    }
  }, [selectedTier, selectedTierOffer, stripe]);

  const selectTier = useCallback((tier: tLeadAgentSelectableTier) => {
    setSelectedTier(tier);
    // Switching tier returns to the confirm step (a prior decline/error for the
    // old tier no longer applies).
    setView({ status: 'confirm' });
  }, []);

  const confirm = useCallback((): boolean => {
    // Synchronous latch pre-check mirroring runConfirm's own guard: everything
    // between here and runConfirm setting the latch runs in the same tick (no
    // await), so a second same-tick call sees the latch and gets `false`. Lets
    // the caller emit UPGRADE_CONFIRMED exactly once per real charge attempt.
    if (inFlightRef.current || !selectedTierOffer) return false;
    void runConfirm();
    return true;
  }, [runConfirm, selectedTierOffer]);

  const confirmDisabled = selectedTierOffer === null || view.status === 'processing';

  return {
    projection,
    retryProjection,
    view,
    tiers,
    selectedTier,
    selectedTierOffer,
    confirmDisabled,
    selectTier,
    confirm,
  };
}
